import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 基础学科与学术教育 - 横屏「课堂黑板」风格
 *
 * 视觉特点：深绿黑板 + 粉笔质感文字 + 手绘风格线条 + 粉笔灰尘粒子
 * 适合：数学公式推导、物理定律讲解、化学方程式等需要"板书"感的内容
 * 尺寸：1920x1080 横屏
 */

interface ChalkStep {
  title: string;
  content: string;
}

interface AcademicChalkboardProps {
  subject: string;
  topic: string;
  subtitle: string;
  steps: ChalkStep[];
  keyFormula: string;
  conclusion: string;
  chalkColor?: string;
}

const DEFAULT_PROPS: AcademicChalkboardProps = {
  subject: '高中数学',
  topic: '等差数列求和公式推导',
  subtitle: 'Sum of Arithmetic Sequence',
  steps: [
    { title: '定义', content: '首项 a₁，公差 d，第 n 项 aₙ = a₁ + (n-1)d' },
    { title: '正序求和', content: 'Sₙ = a₁ + a₂ + a₃ + ... + aₙ' },
    { title: '倒序求和', content: 'Sₙ = aₙ + aₙ₋₁ + ... + a₁' },
    { title: '两式相加', content: '2Sₙ = n(a₁ + aₙ)' },
  ],
  keyFormula: 'Sₙ = n(a₁ + aₙ) / 2',
  conclusion: '倒序相加法是求等差数列和的经典方法',
  chalkColor: '#e8e4d9',
};

const ChalkDust: React.FC<{ count: number }> = ({ count }) => {
  const frame = useCurrentFrame();
  return (
    <>
      {Array.from({ length: count }).map((_, i) => {
        const baseX = (i * 137 + 29) % 1920;
        const speed = 0.3 + (i % 5) * 0.15;
        const y = interpolate((frame * speed + i * 40) % 300, [0, 300], [1080, -10]);
        const drift = Math.sin((frame + i * 20) / 30) * 15;
        const opacity = interpolate((frame * speed + i * 40) % 300, [0, 60, 240, 300], [0, 0.35, 0.35, 0]);
        const size = 2 + (i % 3);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: baseX + drift,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.5)',
              opacity,
              filter: 'blur(0.5px)',
            }}
          />
        );
      })}
    </>
  );
};

export const AcademicChalkboardTemplate: React.FC<Partial<AcademicChalkboardProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const chalk = p.chalkColor!;

  // Title phase (0-50)
  const titleOpacity = interpolate(frame, [0, 25], [0, 1], { extrapolateRight: 'clamp' });
  const titleX = spring({ frame, fps, from: -60, to: 0, config: { damping: 20, stiffness: 80 } });

  // Subject badge
  const badgeScale = spring({ frame: frame - 5, fps, from: 0, to: 1, config: { damping: 12, stiffness: 150, mass: 0.5 } });

  // Steps phase (60+)
  const stepStart = 60;
  const stepInterval = 70;

  // Formula phase
  const formulaStart = stepStart + p.steps.length * stepInterval + 20;
  const formulaProgress = spring({
    frame: frame - formulaStart,
    fps,
    from: 0,
    to: 1,
    config: { damping: 10, stiffness: 80, mass: 1 },
  });
  const formulaOpacity = interpolate(frame, [formulaStart, formulaStart + 20], [0, 1], { extrapolateRight: 'clamp' });

  // Conclusion
  const conclusionStart = formulaStart + 60;
  const conclusionOpacity = interpolate(frame, [conclusionStart, conclusionStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Chalk underline for formula
  const underlineWidth = interpolate(frame, [formulaStart + 30, formulaStart + 60], [0, 100], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(170deg, #2d5016 0%, #1a3a0a 40%, #0f2b04 100%)',
        fontFamily: '"LXGW WenKai", "Zhi Mang Xing", "Ma Shan Zheng", "PingFang SC", serif',
        color: chalk,
        overflow: 'hidden',
      }}
    >
      {/* Chalkboard texture overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px), repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 4px)',
        }}
      />

      {/* Board frame */}
      <div
        style={{
          position: 'absolute',
          inset: 16,
          border: `6px solid #8B6914`,
          borderRadius: 4,
          boxShadow: 'inset 0 0 60px rgba(0,0,0,0.3), 0 0 20px rgba(0,0,0,0.5)',
        }}
      />

      {/* Chalk tray */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 80,
          right: 80,
          height: 12,
          background: 'linear-gradient(180deg, #8B6914, #6B4F10)',
          borderRadius: '0 0 2px 2px',
        }}
      />

      <ChalkDust count={15} />

      {/* Subject badge - top left */}
      <div
        style={{
          position: 'absolute',
          top: 50,
          left: 60,
          transform: `scale(${badgeScale})`,
        }}
      >
        <div
          style={{
            padding: '8px 24px',
            border: `2px solid ${chalk}60`,
            borderRadius: 8,
            fontSize: 26,
            letterSpacing: '0.1em',
            opacity: 0.7,
          }}
        >
          {p.subject}
        </div>
      </div>

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 50,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleOpacity,
          transform: `translateX(${titleX}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 400,
            margin: 0,
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            letterSpacing: '0.05em',
          }}
        >
          {p.topic}
        </h1>
        <p style={{ fontSize: 28, marginTop: 10, opacity: 0.5, fontStyle: 'italic' }}>
          {p.subtitle}
        </p>
      </div>

      {/* Steps - left column */}
      <div
        style={{
          position: 'absolute',
          top: 250,
          left: 70,
          width: '55%',
        }}
      >
        {p.steps.map((step, i) => {
          const start = stepStart + i * stepInterval;
          const writeProgress = interpolate(frame, [start, start + 40], [0, 1], { extrapolateRight: 'clamp' });
          const charsToShow = Math.floor(step.content.length * writeProgress);
          const titleWrite = interpolate(frame, [start, start + 15], [0, 1], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                marginBottom: 30,
                opacity: interpolate(frame, [start, start + 10], [0, 1], { extrapolateRight: 'clamp' }),
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: `2px solid #fbbf24`,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: 18,
                    color: '#fbbf24',
                    fontWeight: 700,
                    opacity: titleWrite,
                  }}
                >
                  {i + 1}
                </div>
                <span
                  style={{
                    fontSize: 30,
                    color: '#fbbf24',
                    fontWeight: 600,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  {step.title.substring(0, Math.floor(step.title.length * titleWrite))}
                </span>
              </div>
              <div
                style={{
                  fontSize: 36,
                  lineHeight: 1.6,
                  paddingLeft: 52,
                  textShadow: '1px 1px 3px rgba(0,0,0,0.2)',
                  fontFamily: '"Times New Roman", Georgia, "LXGW WenKai", serif',
                  fontStyle: 'italic',
                }}
              >
                {step.content.substring(0, charsToShow)}
                {charsToShow < step.content.length && (
                  <span style={{ opacity: frame % 15 < 8 ? 0.8 : 0 }}>|</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Key formula - right side highlight box */}
      <div
        style={{
          position: 'absolute',
          top: 250,
          right: 70,
          width: '35%',
          opacity: formulaOpacity,
          transform: `scale(${0.9 + formulaProgress * 0.1})`,
        }}
      >
        <div
          style={{
            border: `3px solid #fbbf2480`,
            borderRadius: 16,
            padding: '40px 32px',
            background: 'rgba(251, 191, 36, 0.05)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 30, color: '#fbbf24', marginBottom: 16, opacity: 0.7, letterSpacing: '0.15em' }}>
            ★ 核心公式 ★
          </div>
          <div
            style={{
              fontSize: 52,
              fontFamily: '"Times New Roman", Georgia, serif',
              fontStyle: 'italic',
              fontWeight: 700,
              textShadow: '2px 2px 6px rgba(0,0,0,0.3)',
              position: 'relative',
              display: 'inline-block',
            }}
          >
            {p.keyFormula}
            {/* Chalk underline */}
            <div
              style={{
                position: 'absolute',
                bottom: -8,
                left: `${(100 - underlineWidth) / 2}%`,
                width: `${underlineWidth}%`,
                height: 4,
                background: '#fbbf24',
                borderRadius: 2,
                opacity: 0.8,
              }}
            />
          </div>
        </div>

        {/* Decorative chalk drawing - simple diagram */}
        <div
          style={{
            marginTop: 40,
            opacity: interpolate(frame, [formulaStart + 40, formulaStart + 60], [0, 0.5], { extrapolateRight: 'clamp' }),
          }}
        >
          <svg width="100%" height="160" viewBox="0 0 400 160">
            <text x="200" y="30" fill="#fbbf2480" fontSize="30" textAnchor="middle" fontStyle="italic">
              a₁, a₂, a₃ ... aₙ
            </text>
            {/* arrows */}
            <line x1="60" y1="50" x2="340" y2="50" stroke={`${chalk}30`} strokeWidth="5" strokeDasharray="6 4" />
            <line x1="60" y1="90" x2="340" y2="90" stroke={`${chalk}30`} strokeWidth="5" strokeDasharray="6 4" />
            <text x="-10" y="55" fill={`${chalk}50`} fontSize="30">正序</text>
            <text x="-10" y="95" fill={`${chalk}50`} fontSize="30">倒序</text>
            <text x="345" y="55" fill={`${chalk}40`} fontSize="30">→</text>
            <text x="345" y="95" fill={`${chalk}40`} fontSize="30">←</text>
            {/* sum indicator */}
            <line x1="200" y1="110" x2="200" y2="130" stroke="#fbbf2460" strokeWidth="1" />
            <text x="200" y="150" fill="#fbbf24" fontSize="30" textAnchor="middle" fontWeight="bold">
              2Sₙ = n(a₁+aₙ)
            </text>
          </svg>
        </div>
      </div>

      {/* Conclusion */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: conclusionOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '12px 40px',
            borderTop: `2px solid ${chalk}30`,
            borderBottom: `2px solid ${chalk}30`,
            fontSize: 28,
            opacity: 0.7,
            letterSpacing: '0.05em',
          }}
        >
          💡 {p.conclusion}
        </div>
      </div>

      {/* Progress bar disguised as chalk line */}
      <div
        style={{
          position: 'absolute',
          bottom: 26,
          left: 80,
          width: `${interpolate(frame, [0, 500], [0, 1920 - 160], { extrapolateRight: 'clamp' })}px`,
          height: 3,
          background: `${chalk}25`,
          borderRadius: 2,
        }}
      />
    </AbsoluteFill>
  );
};
